/**
 * Resolve the WAF Monetization demo's endpoints for the current environment.
 *
 * LOCAL (npm start): prod-env values are empty, so we use the CRA proxy paths
 *   /api/wafmon/agent  → http://localhost:8080  (the agent's AgentCore SSE server)
 *   /api/wafmon/seller → http://localhost:8402  (the local seller / edge emulation)
 *   The "monetized vs blocked" toggle is SESSION-SCOPED: both edges point at the same
 *   local seller, and each chat call sends the toggle state as a `posture` field in the
 *   invoke body (agent → `X-Waf-Posture` header → seller honors it per-request). So the
 *   toggle is real AND isolated per session locally — no global server flip.
 *
 * DEPLOYED: prod-env carries the real CloudFront URLs — the invoker, the two seller
 *   distros (WAF-monetized + WAF-block-all), and the sessions API.
 *
 * x402 is the first monetization protocol wired here; the brand is "WAF Monetization"
 * so additional protocols can join later without renaming the feature.
 */
import {
  X402_INVOKER_ENDPOINT,
  X402_DISTRO_MONETIZED_BASE,
  X402_DISTRO_BLOCKED_BASE,
  X402_SESSIONS_ENDPOINT,
} from '../../resources/prod-env.jsx';

const LOCAL_INVOKER = '/api/wafmon/agent';
// What the AGENT (running on the host in local mode) uses to reach the seller —
// must be an absolute URL the agent process can resolve, NOT the browser proxy path.
const LOCAL_SELLER_FOR_AGENT = 'http://127.0.0.1:8402';
// What the BROWSER uses (posture flip, wallet balances, storefront reads) via proxy.
const LOCAL_SELLER_FOR_BROWSER = '/api/wafmon/seller';

export function getConfig() {
  const deployed = Boolean(X402_INVOKER_ENDPOINT);
  return {
    deployed,
    invokerUrl: deployed ? X402_INVOKER_ENDPOINT : LOCAL_INVOKER,
    sessionsUrl: X402_SESSIONS_ENDPOINT || '',
    // Browser-reachable seller base (storefront reads + wallet balances + posture).
    sellerBrowserBase: deployed ? X402_DISTRO_MONETIZED_BASE : LOCAL_SELLER_FOR_BROWSER,
    // The two edges the toggle switches between (the base URL SENT TO THE AGENT).
    // Deployed: two distinct CloudFront distros (the URL itself encodes monetize vs
    // block). Local: the SAME in-process seller for both — the per-session `posture`
    // field in each chat call decides the outcome (see chatWithAgent → X-Waf-Posture).
    edges: {
      monetized: deployed ? X402_DISTRO_MONETIZED_BASE : LOCAL_SELLER_FOR_AGENT,
      blocked: deployed ? X402_DISTRO_BLOCKED_BASE : LOCAL_SELLER_FOR_AGENT,
    },
  };
}

/**
 * Base URL for the storefront images (movie posters, news photos). Served free — the
 * demo stack owns the JPEGs (backend/x402-payments/assets/img) and serves them at /img/*:
 *   DEPLOYED → an /img/* CloudFront behavior on the seller distro (S3 origin), and
 *   LOCAL    → the local seller (:8402) via the /api/wafmon/seller proxy.
 * Either way this is the same base the storefronts already read for /browse/*.
 */
export function imgBase() {
  return getConfig().sellerBrowserBase;
}

/** User-Agents: a bot UA hits the priced/blocked edge; a human is free everywhere. */
export const BOT_UA =
  'Mozilla/5.0 (compatible; Bedrockbot/1.0; +https://developer.amazon.com/bedrock)';
export const HUMAN_UA = 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) Safari/605.1';
